import React, { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { useElementWidth } from '../../../../../shared/hooks/useElementWidth';
import { selectCloudRecordingStatus, selectIsCloudRecordingAvailable } from '../../../features/cloud-recording';
import { selectHasCloudRecordingPermission } from '../../../features/permissions';
import { CloudRecordingButton, CloudRecordingOption } from '../toolbar-controls/cloud-recording';
import { useRequiredRecordingButtonWidth } from './use-required-recording-button-width';

export interface UseCloudRecordingControlOptions {
    canButtonAllocate?: boolean;
}

export function useCloudRecordingControl(availableSpace: number, options: UseCloudRecordingControlOptions = {}) {
    const { canButtonAllocate } = { canButtonAllocate: true, ...options };

    const isCloudRecordingAvailable = useSelector(selectIsCloudRecordingAvailable);
    const hasPermission = useSelector(selectHasCloudRecordingPermission);
    const cloudRecordingStatus = useSelector(selectCloudRecordingStatus);

    const [ref, width] = useElementWidth();

    const { button, option } = useMemo(() => {
        return {
            button: (
                <div ref={ref} key="cloudRecording">
                    <CloudRecordingButton />
                </div>
            ),
            option: <CloudRecordingOption key="cloudRecording" />,
        };
    }, [ref]);

    const requiredWidth = useRequiredRecordingButtonWidth(width);
    const isEnoughSpace = availableSpace > requiredWidth;

    const isAvailable = isCloudRecordingAvailable && hasPermission && cloudRecordingStatus;
    const isButton = isEnoughSpace && cloudRecordingStatus !== 'TURNED_OFF' && canButtonAllocate;

    const cloudRecordingButton = isAvailable && isButton ? button : null;
    const cloudRecordingOption = isAvailable && !isButton ? option : null;

    const cloudRecordingButtonWidth = cloudRecordingButton ? requiredWidth : 0;

    return { cloudRecordingButton, cloudRecordingOption, cloudRecordingButtonWidth };
}
